<template>
  <div class="relative text-black">
    <!-- grid overlay -->

    <FlexuilibGrid
      class="text-black/10 fixed inset-0 -z-10"
      :thickness="1"
      :bgSize="40"
      maskWidth="50vw"
    />

    <div class="mx-auto max-w-7xl md:px-8 xl:px-0">
      <FlexuilibHero
        class="md:py-24 sm:py-32 lg:py-40"
        h1Start="Dobrze zrobiona strona to"
        h1Accent="inwestycja, nie koszt"
        h2="Masz wiedzę i doświadczenie. Ale gdzie jest Twoja strona?"
        body="Znasz się na tym, co robisz. Ale Twoi przyszli klienci nie wiedzą, że istniejesz. Brakuje tylko jednego – porządnej strony internetowej. Ty skupiasz się na swojej pracy, a my tworzymy stronę, która przyciąga klientów."
        :cta1="{
          label: 'Porozmawiajmy',
          path: '/kontakt',
        }"
        :cta2="{
          label: 'Stwórz stronę',
          path: '/cennik',
        }"
        :style="{
          '--color-iq-primary': '#dc2626',
          '--color-iq-secondary': '#dc9999',
          '--color-iq-header-color': '#303030',
        }"
      >
        <!-- Responsive preload for the LCP image (note %2C for commas) -->

        <FlexuilibLcpImage
          src="/layout/online-shop.jpg"
          sizes="xs:100vw  lg:1/2"
          class="md:rounded-iq-roundness w-full object-cover aspect-square shadow-lg shadow-black/20"
          :opts="{ fit: 'cover' }"
        />
      </FlexuilibHero>

      <FlexuilibH2CardImageSlim
        class="mt-12 md:mt-0"
        h2="Prosta, szybka i bezproblemowa strona na początek"
        body="Dopiero zaczynasz z biznesem i nie masz czasu na komplikacje? Tworzymy strony, które po prostu działają – są łatwe w obsłudze, szybko się ładują i nie sprawiają kłopotów. Idealne na dobry start, bez zbędnych kroków. Skup się na swoim biznesie – my zadbamy o stronę."
        :image="{ src: '/layout/website.jpg', alt: 'Podgląd prostej i szybkiej strony' }"
        :cta="{
          label: 'Sprawdź ceny',
          path: '/cennik',
          type: 'primary',
          icon: 'solar:tag-price-bold',
        }"
        data-aos="fade-right"
        data-aos-easing="ease-in-out"
        data-aos-duration="500"
      />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-10 my-32">
        <FlexuilibCardLottie
          class="p-4"
          data-aos="fade-up"
          data-aos-duration="1000"
          lottie="/lottie/writing.json"
          title="Teksty, które trafiają w sedno"
          description="Nie musisz zgadywać, co napisać na stronie. My tworzymy treści, które mówią językiem Twoich klientów. Prosto, zrozumiale i tak, by od razu wiedzieli, że trafili w dobre miejsce. Dzięki temu Twoja strona sprzedaje, nawet gdy Ty śpisz. "
          :cta="{
            label: 'Dowiedz się więcej o copywritingu',
            path: '/pomoc/co-sprawia-ze-tekst-na-stronie-internetowej-dziala-skutecznie',
            type: 'secondary',
            icon: 'material-symbols:edit-outline-sharp',
          }"
        />

        <FlexuilibCardLottie
          lottie="/lottie/map.json"
          title="Daj się znaleźć i wzbudź zaufanie"
          :cta="{
            label: 'Jak zaistnieć w Google Maps?',
            path: '/pomoc/co-zrobic-by-firma-wyswietlala-sie-w-google-maps',
            icon: 'material-symbols:add-location-alt-outline',
          }"
          class="p-4"
          data-aos="fade-up"
          data-aos-duration="1000"
          data-aos-delay="400"
        >
          <template #description>
            Jeśli nie ma Cię w internecie, wielu klientów nawet nie pomyśli, że Twój
            biznes istnieje. Aż
            <strong>
              75% ludzi ocenia wiarygodność firmy na podstawie strony internetowej,
            </strong>
            a
            <strong> 97% szuka lokalnych usług online. </strong>
            Strona to nie tylko widoczność – to dowód, że działasz naprawdę. My zadbamy o
            to, żeby Twoja firma wyglądała profesjonalnie i była tam, gdzie klienci jej
            szukają – w sieci.
          </template>
        </FlexuilibCardLottie>
        <FlexuilibCardH2Vertical
          h2="Szybki start bez zbędnych pytań"
          body="Nie musisz czekać tygodniami na stronę. U nas wszystko dzieje się błyskawicznie –
        uruchamiamy Twoją witrynę tak szybko, jak potrzebujesz, nawet w 7 dni. Minimum
        formalności, maksimum działania. Startujesz z biznesem? My startujemy z Twoją
        stroną."
          lottie="/lottie/launch.json"
          :cta="{
            label: 'Zobacz jak tworzymy strony',
            path: '/proces-tworzenia-strony',
            icon: 'material-symbols:play-circle-outline',
            type: 'secondary',
          }"
          subtitle="Uruchomienie nawet w 7 dni."
          class="col-span-1 md:col-span-2"
          data-aos="fade-up"
        />
      </div>
      <FlexuilibH2CardMockup
        class="my-32 px-2 md:px-0"
        h2="Strona, która ładuje się zanim zdążysz mrugnąć"
      >
        <template #body>
          <p class="mt-6 text-lg text-justify">
            Strony, które tworzymy, ładują się w mgnieniu oka – średnio w mniej niż 2
            sekundy. To ważne, bo
            <strong>
              ponad połowa użytkowników opuści stronę, jeśli ładuje się dłużej niż 3
              sekundy.
            </strong>
            A każda dodatkowa sekunda może
            <strong> zmniejszyć konwersję nawet o 20%. </strong>
            Szybkość działania to więcej wejść, więcej zaufania i więcej klientów.
          </p>
        </template>
      </FlexuilibH2CardMockup>

      <FlexuilibGoogleMap
        class="max-w-7xl mx-auto my-32"
        h2="Bądź widoczny tam, gdzie klienci szukają – w Google Maps"
        mapSrc="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2504.905176080762!2d17.029563377091893!3d51.110209671725706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x470e40c57467f87b%3A0xe72be8d8d320d5d8!2sWroc%C5%82awski%20Rynek!5e0!3m2!1spl!2spl!4v1760445395023!5m2!1spl!2spl"
        data-aos="flip-down"
        data-aos-duration="1000"
      >
        <template #body>
          <p class="mt-4 text-justify">
            Większość ludzi, zanim kupi, szuka w Google.
            <strong> Aż 86% klientów sprawdza lokalne firmy w Mapach Google, </strong>
            a
            <strong> 76% z nich odwiedza miejsce jeszcze tego samego dnia. </strong>

            Jeśli Twojej firmy tam nie ma, to tak, jakby nie istniała. Dlatego pomagamy Ci
            zaistnieć w wyszukiwarkach i mapach – żeby klienci mogli Cię łatwo znaleźć,
            zaufać i trafić prosto do Ciebie.
          </p>
        </template>
      </FlexuilibGoogleMap>
      <Progressive class="px-2 md:px-0 my-32" />
    </div>

    <div
      class="max-w-7xl mx-auto min-h-[60vh] my-32 grid grid-cols-1 md:grid-cols-4 relative"
    >
      <div
        class="flex flex-row items-center justify-center gap-10 absolute md:relative top-0 inset-x-0 opacity-50 -translate-y-16 md:-translate-y-0 md:opacity-100"
      >
        <FlexuilibMagicImg
          src="/layout/eye.gif"
          alt="Oko patrzące na stronę internetową"
          fit="cover"
          class="w-32 h-32 object-cover rounded-iq-roundness"
        />

        <FlexuilibMagicImg
          src="/layout/eye.gif"
          alt="Oko patrzące na stronę internetową"
          fit="cover"
          class="w-32 h-32 object-cover rounded-iq-roundness"
        />
      </div>

      <FlexuilibH2
        class="md:col-span-3 space-y-6 h-full p-8 md:p-16"
        h2="Otwórz oczy na to, co widzą (i robią) Twoi klienci"
        :cta="{
          label: 'Przeczytaj więcej o narzędziach analitycznych',
          path: '/pomoc/co-to-jest-google-search-console-i-google-analytics',
        }"
      >
        <template #body>
          Zamiast zgadywać, dajemy Ci konkretne dane. Dzięki Google Analytics i Google
          Search Console widzisz, ile osób odwiedza Twoją stronę, skąd przychodzą i co ich
          interesuje. To ważne, bo firmy, które regularnie analizują dane,
          <strong> zwiększają swoje przychody o 20% szybciej. </strong>

          A my wszystko ustawimy za Ciebie – żebyś wiedział, co działa i co poprawić.
        </template>
      </FlexuilibH2>
    </div>

    <Rules
      class="max-w-7xl mx-auto iq-card-solid p-10 bg-sky-700 my-32"
      :to="560"
      :style="{
        '--color-iq-header-color': '#fff',
        '--color-iq-paragraph-color': '#f3f4f6',
      }"
    />
  </div>
</template>
